import { useState, useEffect } from 'react';
import api from '../../services/api';

const METHOD_LABELS: Record<string, { label: string; color: string }> = {
  stripe_card: { label: 'Stripe Card', color: 'bg-purple-100 text-purple-800' },
  stripe_ach: { label: 'Stripe ACH', color: 'bg-indigo-100 text-indigo-800' },
  authorize_net: { label: 'Authorize.net', color: 'bg-amber-100 text-amber-800' },
  helcim_ach: { label: 'Helcim ACH', color: 'bg-teal-100 text-teal-800' },
  check: { label: 'Check', color: 'bg-yellow-100 text-yellow-800' },
  wire_transfer: { label: 'Wire', color: 'bg-cyan-100 text-cyan-800' },
  cash: { label: 'Cash', color: 'bg-green-100 text-green-800' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-800' },
};

const TYPE_LABELS: Record<string, string> = {
  deposit: 'Deposit',
  balance: 'Balance',
  full: 'Full Payment',
};

interface PaymentRecord {
  id: string;
  orderNumber: string;
  companyName: string;
  amount: number;
  preDiscountAmount: number | null;
  achDiscountPercent: number | null;
  achDiscountAmount: number | null;
  paymentMethod: string;
  paymentType: string;
  status: string;
  transactionId: string | null;
  processingFee: number | null;
  orderTotal: number;
  date: string;
}

export default function PaymentAuditPage() {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const pageSize = 50;

  useEffect(() => {
    fetchPayments();
  }, [page, search, paymentMethod, dateFrom, dateTo]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const result = await api.paymentAudit.list({ page, pageSize, search, paymentMethod, dateFrom, dateTo });
      setPayments(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error('Failed to load payments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const totalPages = Math.ceil(total / pageSize);

  const totalAmount = payments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payment Audit Log</h1>
          <p className="text-sm text-gray-500 mt-1">All payment transactions across Authorize.net, Helcim, and Stripe</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Page Total</div>
          <div className="text-xl font-bold text-gray-900">{fmt(totalAmount)}</div>
          <div className="text-xs text-gray-400">{total} transactions</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <input
              type="text"
              placeholder="Search by order # or company..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); }}}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <select
            value={paymentMethod}
            onChange={e => { setPaymentMethod(e.target.value); setPage(1); }}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">All Methods</option>
            <option value="authorize_net">Authorize.net</option>
            <option value="helcim_ach">Helcim ACH</option>
            <option value="stripe_card">Stripe Card</option>
            <option value="stripe_ach">Stripe ACH</option>
            <option value="check">Check</option>
            <option value="other">Other</option>
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} className="px-3 py-2 border rounded-lg text-sm" />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} className="px-3 py-2 border rounded-lg text-sm" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">ACH Disc.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : payments.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">No payments found</td></tr>
              ) : payments.map(p => {
                const method = METHOD_LABELS[p.paymentMethod] || { label: p.paymentMethod || '—', color: 'bg-gray-100 text-gray-800' };
                const txId = p.transactionId && p.transactionId !== 'undefined' && p.transactionId !== 'unknown' ? p.transactionId : null;
                return (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {new Date(p.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-blue-600 whitespace-nowrap">
                      <a href={`/orders/${p.orderNumber}`} className="hover:underline">{p.orderNumber}</a>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-[200px] truncate">{p.companyName}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${method.color}`}>
                        {method.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{TYPE_LABELS[p.paymentType] || p.paymentType || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium whitespace-nowrap">{fmt(p.amount)}</td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      {p.achDiscountAmount && p.achDiscountAmount > 0
                        ? <span className="text-green-600">-{fmt(p.achDiscountAmount)}</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono max-w-[160px] truncate" title={txId || ''}>
                      {txId || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                      {p.processingFee ? fmt(p.processingFee) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.status === 'completed' ? 'bg-green-100 text-green-800'
                        : p.status === 'refunded' ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                      }`}>{p.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <div className="text-sm text-gray-500">
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </div>
            <div className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-white">Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 border rounded text-sm disabled:opacity-50 hover:bg-white">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
